/**
 * A gate punch beyond a radius of the site's pin (piece 34, *Time capture*; piece 15, *Settings › HR*; decision
 * 12931, 13230; T3856, T3888). 🔒 The radius is `hr.punchRadiusMeters`; a gate punch whose kept distance stands farther
 * than it from the pin of the site the Work Order belongs to carries a line for the approver on Time Awaiting Approval.
 * 🔨 It never blocks the punch, it never shows a coordinate, and a site with no pin or a punch with no coordinates says nothing.
 */

import { coordinatesOf, type GeoPoint } from '../records/coordinates';
import { formatMeters, metersBetween } from '../pages/checkIn';
import { fill, t } from '../strings';

/** The seeded radius, in meters: the owner changes it on Settings › HR. */
export const HR_PUNCH_RADIUS_DEFAULT_METRES = 500;
/** The smallest and the largest radius kept: phone location is good to tens of meters, and a plant fits inside fifty kilometers. */
export const HR_PUNCH_RADIUS_MINIMUM_METRES = 50;
export const HR_PUNCH_RADIUS_MAXIMUM_METRES = 50_000;

/** The radius is a whole number of meters from 50 to 50,000, never blank, because the queue reads it on every draw. */
export function hrPunchRadiusProblem(value: unknown): string | null {
  const meters = typeof value === 'number' ? value : Number(value);
  if (value === null || value === undefined || value === '' || !Number.isInteger(meters) || meters < HR_PUNCH_RADIUS_MINIMUM_METRES || meters > HR_PUNCH_RADIUS_MAXIMUM_METRES) {
    return `The punch radius is a whole number of meters from ${HR_PUNCH_RADIUS_MINIMUM_METRES} to ${HR_PUNCH_RADIUS_MAXIMUM_METRES.toLocaleString('en-US')}.`;
  }
  return null;
}

/** The radius in force: the stored number where it is a valid one, else the seeded default. */
export function punchRadiusOf(stored: unknown): number {
  return hrPunchRadiusProblem(stored) === null ? Number(stored) : HR_PUNCH_RADIUS_DEFAULT_METRES;
}

/**
 * 🔒 How far a punch stood from the site's pin, in whole meters, which the gate's sign-in keeps on the entry at the
 * punch (decision 13230). The distance is the check-in's own `metersBetween`, and the pin comes from the Location's one
 * `locationPoint` rule (decision 0674). A pin or a punch missing either coordinate gives null: nothing is said about
 * what was not captured.
 */
export function punchDistanceMeters(punch: { latitude: number | null; longitude: number | null }, pin: GeoPoint | null): number | null {
  const at = coordinatesOf(punch.latitude, punch.longitude);
  if (!at || !pin) return null;
  return Math.round(metersBetween(at, pin));
}

/**
 * 🔒 The kept distance where it stands beyond the radius, else null (decision 12931). The queue compares the figure
 * the gate kept with the radius in force and never recomputes it from a pin (decision 13230); an entry that kept none
 * says nothing.
 */
export function punchBeyondRadius(keptMeters: number | null, radiusMeters: number): number | null {
  return keptMeters !== null && keptMeters > radiusMeters ? keptMeters : null;
}

/** One gate punch beyond the radius, as the approver's line is drawn: the job and the distance, never a coordinate. */
export interface FarPunch {
  entryId: string;
  workOrderNumber: string | null;
  meters: number;
}

/** The approver's line: *Signed in 1.2 km from the site's pin · W-1042*. */
export function farPunchLine(punch: Pick<FarPunch, 'workOrderNumber' | 'meters'>): string {
  const line = fill(t('timeApproval.farPunch'), { distance: formatMeters(punch.meters) });
  return punch.workOrderNumber ? `${line} · ${punch.workOrderNumber}` : line;
}
